import type { DashboardPulse } from "@/api/db/dashboard-pulse";
import { DashboardLabels } from "@/lib/labels";
import {
	DashboardCard,
	DashboardCardBody,
	DashboardCardHeader,
	DashboardCardTitle,
} from "./dashboard-card";

const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6);
const COUNT = new Intl.NumberFormat("pt-BR");

function cellColor(value: number, max: number) {
	if (value === 0) {
		return "color-mix(in oklch, var(--info) 5%, transparent)";
	}

	const intensity = Math.sqrt(value / max);

	return `color-mix(in oklch, var(--info) ${(10 + 90 * intensity).toFixed(1)}%, transparent)`;
}

export function WhatsappHeatmap({ cells }: { cells: DashboardPulse["heatmap"] }) {
	const byKey = new Map(cells.map((cell) => [`${cell.dow}-${cell.hour}`, cell.total]));
	const max = Math.max(1, ...cells.map((cell) => cell.total));
	const total = cells.reduce((sum, cell) => sum + cell.total, 0);

	return (
		<DashboardCard data-component="dashboard-heatmap">
			<DashboardCardHeader>
				<DashboardCardTitle>{DashboardLabels.heatmap.title}</DashboardCardTitle>

				<span className="font-mono text-xs text-muted-foreground">
					{COUNT.format(total)} {DashboardLabels.heatmap.messages}
				</span>
			</DashboardCardHeader>

			<DashboardCardBody className="overflow-x-auto">
				<div className="grid min-w-140 grid-cols-[32px_1fr] gap-x-2">
					<div className="grid grid-rows-7 gap-1">
						{DOW_ORDER.map((dow) => (
							<span
								key={dow}
								className="flex h-6 items-center font-mono text-xs text-muted-foreground"
							>
								{DashboardLabels.heatmap.days[dow]}
							</span>
						))}
					</div>

					<div className="flex flex-col gap-1">
						{DOW_ORDER.map((dow) => (
							<div key={dow} className="grid grid-cols-16 gap-1">
								{HOURS.map((hour) => {
									const value = byKey.get(`${dow}-${hour}`) ?? 0;

									return (
										<div
											key={hour}
											data-slot="heat-cell"
											data-dow={dow}
											data-hour={hour}
											data-total={value}
											title={`${DashboardLabels.heatmap.days[dow]} ${hour}h: ${COUNT.format(value)}`}
											className="h-6 rounded-sm"
											style={{ backgroundColor: cellColor(value, max) }}
										/>
									);
								})}
							</div>
						))}

						<div className="grid grid-cols-16 gap-1">
							{HOURS.map((hour) => (
								<span key={hour} className="text-center font-mono text-xs text-muted-foreground">
									{hour % 3 === 0 && `${hour}h`}
								</span>
							))}
						</div>
					</div>
				</div>
			</DashboardCardBody>
		</DashboardCard>
	);
}
